/**
 * SupplierJourneyDefinition (AJIP-16.02.008 · WS1)
 * ---------------------------------------------------------------------
 * Version 2 enriched Supplier Journey. Registered via
 * RegistrationService.replace() by the SupplierJourneyOrchestrator.
 *
 * Founder Rulings honoured:
 *   S1  — Audience is exactly ['authenticated', 'authorised-supplier'].
 *   S2  — persistencePolicyRef is exactly 'supplier:v2:standard'.
 *   S3  — NO `no-reentry-after:completed`. Suppliers may pursue
 *         multiple independent objectives; re-entry after completion
 *         is authorised.
 *   S7  — Organisation ownership is NOT embedded in this canonical
 *         shared Journey Definition. Ownership, representation
 *         authority and permissions are evaluated deterministically
 *         at runtime by SupplierJourneyOwnership (WS runtime).
 *
 * Additional locked directive:
 *   The Supplier Journey Definition is a shared canonical asset. It
 *   MUST NOT contain organisation-specific data, permission lists,
 *   or verification outcomes. It carries structural configuration
 *   only.
 */
import { STATES } from '../core/JourneyState';

const buildSupplierDefinition = ({ version = 2, nowFn = () => new Date().toISOString() } = {}) => ({
  journeyId: 'supplier',
  journeyVersion: version,
  journeyName: 'Supplier Journey',
  journeyDescription:
    'Governed supplier pathway: onboarding → claim → verification outcome recording → opportunity → quote submission → objective completion. Multi-objective re-entrant.',
  journeyType: 'supplier',
  journeyCategory: 'verification',
  // Founder Ruling S1 — audience is exactly this pair.
  journeyAudience: ['authenticated', 'authorised-supplier'],
  registrationStatus: 'registered',
  requiredFeatureFlags: ['JOURNEYS_ENABLED'],
  entryConditions: [],
  // Founder Ruling S3 — NO `no-reentry-after:completed`.
  // Founder Ruling S5 — Buyer and Supplier instances remain
  // completely separate. The `conflicts-with:buyer` rule mirrors the
  // Buyer Journey's `conflicts-with:supplier` symmetry at the
  // eligibility layer without inspecting Buyer state directly.
  eligibilityRules: ['conflicts-with:buyer'],
  permissionRequirements: [],
  initialState: STATES.NOT_STARTED,
  supportedStates: [
    STATES.NOT_STARTED, STATES.STARTED, STATES.IN_PROGRESS,
    STATES.WAITING, STATES.BLOCKED, STATES.COMPLETED,
    STATES.CANCELLED, STATES.FAILED,
  ],
  transitionConfiguration: [
    { from: STATES.STARTED, event: 'proceed', to: STATES.IN_PROGRESS },
    { from: STATES.IN_PROGRESS, event: 'wait', to: STATES.WAITING },
    { from: STATES.WAITING, event: 'resume', to: STATES.IN_PROGRESS },
    { from: STATES.IN_PROGRESS, event: 'complete', to: STATES.COMPLETED },
    { from: STATES.IN_PROGRESS, event: 'cancel', to: STATES.CANCELLED },
    { from: STATES.IN_PROGRESS, event: 'fail', to: STATES.FAILED },
    { from: STATES.WAITING, event: 'cancel', to: STATES.CANCELLED },
    { from: STATES.STARTED, event: 'cancel', to: STATES.CANCELLED },
  ],
  // Founder Ruling S2 — exact internal, provider-agnostic reference.
  persistencePolicyRef: 'supplier:v2:standard',
  retentionPolicyRef: 'supplier:v2:standard',
  priority: 30,
  tags: ['supplier', 'verification', 'ajip-16.02.008'],
  canonReference: 'AJIP-16.02.008',
  owner: 'Founder',
  createdAt: nowFn(),
  updatedAt: nowFn(),
  deprecationStatus: 'none',
  replacementJourneyId: null,
});

export { buildSupplierDefinition };
